/**
 * ViewOnlyBadge Component
 * Shows "للعرض فقط" (View Only) badge for executive (Decision Maker) users
 */
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

// Eye icon for view-only indicator
const EyeIcon = ({ size = 14, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

interface ViewOnlyBadgeProps {
  /** Custom label text (default: uses t('common.viewOnly')) */
  label?: string;
  /** Badge size variant */
  size?: 'small' | 'medium' | 'large';
  /** Custom style */
  style?: object;
}

const ViewOnlyBadge: React.FC<ViewOnlyBadgeProps> = ({
  label,
  size = 'medium',
  style,
}) => {
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();

  const displayLabel = label ?? t('common.viewOnly');

  // Theme colors for dark mode
  const themeColors = {
    background: isDark ? colors.backgroundAlt : '#F3F4F6',
    text: colors.textMuted,
    icon: colors.textMuted,
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          container: { paddingHorizontal: 8, paddingVertical: 4, gap: 4 },
          text: { fontSize: 10 },
          iconSize: 12,
        };
      case 'large':
        return {
          container: { paddingHorizontal: 16, paddingVertical: 8, gap: 8 },
          text: { fontSize: 14 },
          iconSize: 18,
        };
      default: // medium
        return {
          container: { paddingHorizontal: 12, paddingVertical: 6, gap: 6 },
          text: { fontSize: 12 },
          iconSize: 14,
        };
    }
  };

  const sizeStyles = getSizeStyles();

  return (
    <View style={[styles.container, sizeStyles.container, { backgroundColor: themeColors.background }, style]}>
      <EyeIcon size={sizeStyles.iconSize} color={themeColors.icon} />
      <Text style={[styles.text, sizeStyles.text, { color: themeColors.text }]}>{displayLabel}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    borderRadius: 16,
  },
  text: {
    color: '#6B7280',
    fontFamily: FONTS.medium,
  },
});

export default ViewOnlyBadge;
